import { useCallback, useEffect, useState } from 'react';
import type { NodeEntry } from 'slate';
import { Editor, Range, Text } from '../core';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

const UNFOCUSED_SELECTION_DECORATION = { unfocusedSelection: true } as const;

interface UseUnfocusedSelectionHighlightParams {
  editor: Editor;
  selection: Range | null;
  isEditorFocused: boolean;
}

interface UseUnfocusedSelectionHighlight {
  resetUnfocusedSelection: () => void;
  captureUnfocusedSelection: () => void;
  getUnfocusedSelectionDecorations: (entry: NodeEntry) => Range[];
}

/**
 * Tracks the editor's expanded selection while it is unfocused so the leaf renderer
 * (see `usePlugins`) can visually highlight it. Gated behind the
 * `REPORTER_UNFOCUSED_SELECTION_HIGHLIGHT` feature flag; when the flag is off the hook
 * is inert and never produces decorations.
 */
export const useUnfocusedSelectionHighlight = ({
  editor,
  selection,
  isEditorFocused,
}: UseUnfocusedSelectionHighlightParams): UseUnfocusedSelectionHighlight => {
  const [isHighlightEnabled] = useFeatureFlagEnabled(FF.REPORTER_UNFOCUSED_SELECTION_HIGHLIGHT);
  const [unfocusedSelection, setUnfocusedSelection] = useState<Range | null>(null);

  useEffect(() => {
    if (!isHighlightEnabled || isEditorFocused) {
      setUnfocusedSelection(null);
      return;
    }

    if (selection == null) {
      return;
    }

    setUnfocusedSelection(Range.isExpanded(selection) ? selection : null);
  }, [isHighlightEnabled, isEditorFocused, selection]);

  const resetUnfocusedSelection = useCallback(() => {
    setUnfocusedSelection(null);
  }, []);

  const captureUnfocusedSelection = useCallback(() => {
    if (!isHighlightEnabled) {
      return;
    }

    setUnfocusedSelection(
      editor.selection != null && Range.isExpanded(editor.selection) ? editor.selection : null
    );
  }, [editor, isHighlightEnabled]);

  const getUnfocusedSelectionDecorations = useCallback(
    ([node, path]: NodeEntry): Range[] => {
      if (!isHighlightEnabled || unfocusedSelection == null || !Text.isText(node)) {
        return [];
      }

      const nodeRange = Editor.range(editor, path);
      const intersection = Range.intersection(unfocusedSelection, nodeRange);

      if (intersection == null) {
        return [];
      }

      return [{ ...intersection, ...UNFOCUSED_SELECTION_DECORATION }];
    },
    [editor, isHighlightEnabled, unfocusedSelection]
  );

  return {
    resetUnfocusedSelection,
    captureUnfocusedSelection,
    getUnfocusedSelectionDecorations,
  };
};
